import mermaid from "mermaid";
import { useEffect, useId, useRef, useState } from "react";

import { CHART_CARD_HEIGHT_CLASS } from "../../constants";

import AnalyticsMermaidZoomViewport from "./AnalyticsMermaidZoomViewport";
import MermaidLoadingOverlay from "./MermaidLoadingOverlay";
import { normalizeMermaidDiagram } from "./utils/normalizeMermaidDiagram";

import { cn } from "@/lib/utils";

let mermaidInitialized = false;
const MERMAID_ERROR_DIAGRAM_MAX_HEIGHT_CLASS = "max-h-[240px]";

const initializeMermaid = () => {
  if (mermaidInitialized) {
    return;
  }

  mermaid.initialize({
    startOnLoad: false,
    theme: "neutral",
    securityLevel: "loose",
    fontFamily: "inherit",
  });
  mermaidInitialized = true;
};

const AnalyticsMermaidDiagram = ({
  diagram,
  className,
  enableZoom = true,
}: {
  diagram: string;
  className?: string;
  enableZoom?: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const renderId = useId().replace(/:/g, "");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRendering, setIsRendering] = useState(false);
  const normalizedDiagram = normalizeMermaidDiagram(diagram);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let cancelled = false;

    const renderDiagram = async () => {
      setErrorMessage(null);
      setIsRendering(true);
      container.innerHTML = "";

      try {
        initializeMermaid();
        const { svg, bindFunctions } = await mermaid.render(
          `analytics-mermaid-${renderId}`,
          normalizedDiagram,
        );

        if (cancelled) {
          return;
        }

        container.innerHTML = svg;
        bindFunctions?.(container);
        setIsRendering(false);
      } catch (error) {
        if (cancelled) {
          return;
        }

        const message = error instanceof Error ? error.message : "Failed to render diagram";
        setErrorMessage(message);
        setIsRendering(false);
      }
    };

    void renderDiagram();

    return () => {
      cancelled = true;
    };
  }, [normalizedDiagram, renderId]);

  if (errorMessage) {
    return (
      <div
        className={cn(
          "rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800",
          className,
        )}
      >
        <p className="font-medium">Could not render diagram</p>
        <p className="mt-1 text-xs text-red-700">{errorMessage}</p>
        <pre
          className={cn(
            "mt-3 overflow-auto rounded border border-red-100 bg-white p-3 text-xs text-gray-800",
            MERMAID_ERROR_DIAGRAM_MAX_HEIGHT_CLASS,
          )}
        >
          {normalizedDiagram}
        </pre>
      </div>
    );
  }

  return (
    <AnalyticsMermaidZoomViewport
      enabled={enableZoom}
      fitKey={`${normalizedDiagram}:${isRendering ? "rendering" : "ready"}`}
      className={cn("relative w-full", CHART_CARD_HEIGHT_CLASS, className)}
    >
      {isRendering ? <MermaidLoadingOverlay label="Loading diagram..." /> : null}
      <div ref={containerRef} className="inline-block" />
    </AnalyticsMermaidZoomViewport>
  );
};

export default AnalyticsMermaidDiagram;
